// src/services/adherentPhotoServices.js
import axios from "axios";

const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const apiUrl = `${baseUrl}/api/adherents`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Upload/Remplace la photo d'un adhérent.
 * - Traité côté serveur avec sharp: 400x400 (cover), auto-rotate EXIF, format par défaut WEBP.
 * - Retour: { id, photo_url } où photo_url est un chemin public (ex: /uploads/photos_adherents/...).
 *
 * @param {number|string} adherentId
 * @param {File|Blob} file - fichier image sélectionné (input type="file")
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"] - format de sortie souhaité côté serveur
 * @param {function} [options.onUploadProgress] - callback Axios pour la progression
 * @returns {Promise<{id:number, photo_url:string}>}
 */
export const uploadAdherentPhoto = async (adherentId, file, options = {}) => {
  if (!adherentId) throw new Error("adherentId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  form.append("image", file);

  try {
    const { data } = await axios.patch(
      `${apiUrl}/${encodeURIComponent(adherentId)}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
          // Ne pas fixer 'Content-Type' manuellement: Axios le fera avec le boundary
        },
        onUploadProgress,
      }
    );
    return data;
  } catch (error) {
    console.error("[uploadAdherentPhoto] erreur:", error);
    // remonter un message exploitable
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Recadre/zoome une photo existante côté serveur.
 * - Si 'src' est omis, le serveur utilise adherent.photo_url actuel.
 * - x,y,width,height sont en pixels relatifs à l'image source.
 * - Retour: { id, photo_url } (nouvelle vignette 400x400).
 *
 * @param {number|string} adherentId
 * @param {Object} crop
 * @param {number} crop.x
 * @param {number} crop.y
 * @param {number} crop.width
 * @param {number} crop.height
 * @param {"webp"|"jpg"|"png"} [crop.format] - format de sortie
 * @param {string} [crop.src] - chemin public /uploads/... si tu veux recadrer une autre image
 * @returns {Promise<{id:number, photo_url:string}>}
 */
export const cropAdherentPhoto = async (adherentId, crop = {}) => {
  if (!adherentId) throw new Error("adherentId requis");

  const { x, y, width, height, format, src } = crop;
  const nums = [x, y, width, height];
  if (nums.some((v) => typeof v !== "number" || Number.isNaN(v))) {
    throw new Error("x, y, width, height numériques requis");
  }

  const payload = { x, y, width, height };
  if (format) payload.format = format;
  if (src) payload.src = src;

  try {
    const { data } = await axios.patch(
      `${apiUrl}/${encodeURIComponent(adherentId)}/photo/crop`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      }
    );
    return data;
  } catch (error) {
    console.error("[cropAdherentPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Helper: renvoie une URL exploitable dans <img src=...>
 * - Si photo_url commence par /uploads/ => préfixe avec baseUrl
 * - Sinon renvoie tel quel (utile si quelques anciennes photos sont déjà absolues)
 *
 * @param {string} photoUrl
 * @returns {string}
 */
export const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (photoUrl.startsWith("/uploads/")) {
    return `${baseUrl}${photoUrl}`;
  }
  return photoUrl;
};
